"use client"

import * as React from "react"
import { Check, X, LucideIcon } from 'lucide-react'
import {
  UICard,
  UICardContent,
  UICardFooter,
  UICardHeader,
  UICardTitle,
  UICardDescription
} from './ui-card'
import { UIButton } from '../buttons/ui-button'
import { UIBadge } from '../display/ui-badge'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UICardPricingProps
  extends Omit<React.ComponentProps<typeof UICard>, 'children'> {
  planName: string
  description?: string
  price: string | number
  period?: string
  originalPrice?: string | number
  badge?: string
  features: Array<{
    text: string
    included: boolean
  }>
  ctaText?: string
  onCTAClick?: () => void
  ctaVariant?: 'default' | 'accent' | 'outline'
  popular?: boolean
  icon?: LucideIcon
  children?: React.ReactNode
}

/**
 * Pricing Card Component
 * Complete pricing plan display with features and CTA
 * Based on shadcn/ui "Pricing Card" block
 */
export const UICardPricing = React.forwardRef<HTMLDivElement, UICardPricingProps>(
  ({
    planName,
    description,
    price,
    period = "month",
    originalPrice,
    badge,
    features,
    ctaText = "Get Started",
    onCTAClick,
    ctaVariant = 'default',
    popular = false,
    icon: Icon,
    className,
    children,
    ...props
  }, ref) => {
    return (
      <UICard
        ref={ref}
        className={cn(
          "relative w-full max-w-sm",
          popular && "border-accent shadow-md ring-1 ring-accent/20",
          className
        )}
        {...props}
      >
        {/* Popular badge */}
        {popular && (
          <UIBox className="absolute -top-3 left-1/2 -translate-x-1/2">
            <UIBadge variant="accent" className="px-3 py-1">
              Most Popular
            </UIBadge>
          </UIBox>
        )}

        {/* Header */}
        <UICardHeader className="text-center">
          <UIBox className="flex items-center justify-center gap-2">
            {Icon && <Icon className="h-5 w-5 text-accent" />}
<UICardTitle>
              <UITypography variant="h3" size="xl" weight="semibold">
                {planName}
              </UITypography>
            </UICardTitle>
            {badge && (
<UIBadge variant="secondary">
                <UITypography variant="caption" size="xs">
                  {badge}
                </UITypography>
              </UIBadge>
            )}
          </UIBox>

          {description && (
<UICardDescription>
              <UITypography variant="p" size="sm" color="muted">
                {description}
              </UITypography>
            </UICardDescription>
          )}

          {/* Pricing */}
          <UIBox className="mt-4">
            <UIBox className="flex items-baseline justify-center gap-1">
              {originalPrice && (
<UITypography as="span" variant="p" size="sm" color="muted" className="line-through">
                  ${originalPrice}
                </UITypography>
              )}
<UITypography as="span" variant="h1" size="3xl" weight="bold" color="default">
                ${price}
              </UITypography>
<UITypography as="span" variant="p" size="sm" color="muted">
                /{period}
              </UITypography>
            </UIBox>
          </UIBox>
        </UICardHeader>

        {/* Features */}
        <UICardContent className="space-y-4">
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2">
                {feature.included ? (
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                ) : (
                  <X className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )}
<UITypography
                  as="span"
                  variant="p"
                  size="sm"
                  color={feature.included ? "default" : "muted"}
                >
                  {feature.text}
                </UITypography>
              </li>
            ))}
          </ul>

          {/* Custom content */}
          {children}
        </UICardContent>

        {/* CTA */}
        <UICardFooter>
          <UIButton
            variant={popular ? 'accent' : ctaVariant}
            onClick={onCTAClick}
            className="w-full"
          >
            {ctaText}
          </UIButton>
        </UICardFooter>
      </UICard>
    )
  }
)

UICardPricing.displayName = "UICardPricing"